import React, { useEffect, useRef, useState } from 'react';
import './VideoPreview.css';
import { useInViewport } from '../hooks/useInViewport';

interface VideoPreviewProps {
  video: string;
  title: string;
  className?: string;
  playOnHover?: boolean;
  autoPlayWhenVisible?: boolean;
  showBadge?: boolean;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({
  video,
  title,
  className = '',
  playOnHover = true,
  autoPlayWhenVisible = false,
  showBadge = true,
}) => {
  const { ref, isInViewport } = useInViewport<HTMLDivElement>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const shouldLoad = isInViewport || isHovered;

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [video]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !shouldLoad) {
      return;
    }

    if (videoEl.src !== video) {
      videoEl.src = video;
      videoEl.load();
    }
  }, [shouldLoad, video]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !isLoaded) {
      return;
    }

    const shouldPlay = autoPlayWhenVisible || (playOnHover && isHovered);

    if (shouldPlay) {
      videoEl.play().catch(() => {});
      return;
    }

    videoEl.pause();
  }, [autoPlayWhenVisible, playOnHover, isHovered, isLoaded]);

  return (
    <div
      ref={ref}
      className={`video-preview ${className} ${isLoaded ? 'is-loaded' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
    >
      {!isLoaded && !hasError && (
        <div className="video-preview-skeleton" aria-hidden="true">
          <span className="video-preview-shimmer" />
          <span className="video-preview-play">▶</span>
        </div>
      )}

      {hasError && (
        <div className="video-preview-fallback">
          <span className="video-preview-play">▶</span>
          <span className="video-preview-fallback-text">{title}</span>
        </div>
      )}

      {shouldLoad && (
        <video
          ref={videoRef}
          className={`video-preview-element ${isLoaded ? 'visible' : ''}`}
          muted
          playsInline
          loop
          preload="metadata"
          onLoadedData={() => setIsLoaded(true)}
          onCanPlay={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      )}

      {showBadge && isLoaded && (
        <div className="video-preview-badge">
          <span className="video-preview-dot" />
          <span>PREVIEW</span>
        </div>
      )}
    </div>
  );
};

export default VideoPreview;
